import { MapPin, Plus, X } from "lucide-react";

import { Field, FieldError } from "@/components/doctor/onboarding/fields";
import { Hint } from "@/components/doctor/onboarding/steps-profile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useOnboarding } from "@/hooks/doctor/use-onboarding";
import { emptyTimings } from "@/services/onboarding/draft-store";
import { cn } from "@/lib/utils";
import type { DayTiming } from "@/types/onboarding";

export function LocationStep() {
  const { draft, update, errors } = useOnboarding();

  return (
    <div className="space-y-5">
      <Field
        label="Establishment address"
        htmlFor="mapAddress"
        required
        error={errors.mapAddress}
        hint="Patients see this address on your profile and in directions."
      >
        <Textarea
          id="mapAddress"
          rows={3}
          value={draft.mapAddress}
          onChange={(event) => update({ mapAddress: event.target.value })}
          placeholder="2nd Floor, 100 Feet Road, Indiranagar, Bengaluru 560038"
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="Latitude"
          htmlFor="latitude"
          hint="Optional — helps place your pin precisely."
        >
          <Input
            id="latitude"
            value={draft.latitude}
            onChange={(event) => update({ latitude: event.target.value })}
            placeholder="12.9716"
            className="h-11"
          />
        </Field>
        <Field label="Longitude" htmlFor="longitude" hint="Optional.">
          <Input
            id="longitude"
            value={draft.longitude}
            onChange={(event) => update({ longitude: event.target.value })}
            placeholder="77.5946"
            className="h-11"
          />
        </Field>
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-4">
        <MapPin className="size-5 shrink-0 text-primary" />
        <p className="text-xs text-muted-foreground">
          {draft.mapAddress
            ? draft.mapAddress
            : "Your pin preview appears here once you enter an address."}
        </p>
      </div>
    </div>
  );
}

export function TimingsEditor({
  timings,
  onChange,
  disabled,
}: {
  timings: DayTiming[];
  onChange: (next: DayTiming[]) => void;
  disabled?: boolean | undefined;
}) {
  const patch = (day: string, updater: (value: DayTiming) => DayTiming) =>
    onChange(timings.map((entry) => (entry.day === day ? updater(entry) : entry)));

  return (
    <div className={cn("space-y-2", disabled && "pointer-events-none opacity-50")}>
      {timings.map((entry) => (
        <div
          key={entry.day}
          className={cn(
            "rounded-xl border border-border px-4 py-3 transition-colors",
            entry.enabled ? "bg-card" : "bg-muted/40",
          )}
        >
          <div className="flex items-center gap-3">
            <Switch
              id={`timing-${entry.day}`}
              checked={entry.enabled}
              onCheckedChange={(enabled) => patch(entry.day, (value) => ({ ...value, enabled }))}
            />
            <Label htmlFor={`timing-${entry.day}`} className="cursor-pointer text-sm font-medium">
              {entry.day}
            </Label>
            {entry.enabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() =>
                  patch(entry.day, (value) => ({
                    ...value,
                    sessions: [...value.sessions, { from: "17:00", to: "20:00" }],
                  }))
                }
              >
                <Plus className="size-4" /> Add session
              </Button>
            )}
          </div>

          {entry.enabled && (
            <div className="mt-3 space-y-2">
              {entry.sessions.map((session, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="time"
                    aria-label={`${entry.day} session ${index + 1} start`}
                    value={session.from}
                    className="h-9 w-[7.5rem]"
                    onChange={(event) =>
                      patch(entry.day, (value) => ({
                        ...value,
                        sessions: value.sessions.map((item, position) =>
                          position === index ? { ...item, from: event.target.value } : item,
                        ),
                      }))
                    }
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="time"
                    aria-label={`${entry.day} session ${index + 1} end`}
                    value={session.to}
                    className="h-9 w-[7.5rem]"
                    onChange={(event) =>
                      patch(entry.day, (value) => ({
                        ...value,
                        sessions: value.sessions.map((item, position) =>
                          position === index ? { ...item, to: event.target.value } : item,
                        ),
                      }))
                    }
                  />
                  {entry.sessions.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove ${entry.day} session ${index + 1}`}
                      className="text-muted-foreground transition-colors hover:text-destructive"
                      onClick={() =>
                        patch(entry.day, (value) => ({
                          ...value,
                          sessions: value.sessions.filter((_, position) => position !== index),
                        }))
                      }
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function TimingsStep() {
  const { draft, update, errors } = useOnboarding();
  return (
    <div className="space-y-4">
      <Hint>
        Only the first session of each enabled day is sent to the backend today — the documented
        schedule accepts one start and end time per day. Extra sessions are kept in your draft.
      </Hint>
      <TimingsEditor timings={draft.timings} onChange={(timings) => update({ timings })} />
      <FieldError message={errors.timings} />
    </div>
  );
}

export function ConsultantStep() {
  const { draft, update, errors, countryData } = useOnboarding();
  const symbol = countryData?.meta.currencySymbol ?? "₹";
  const consultant = draft.consultant;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between rounded-xl border border-border p-4">
        <div>
          <p className="text-sm font-medium">I also consult at another establishment</p>
          <p className="text-xs text-muted-foreground">
            Add separate fees and hours for your visiting practice.
          </p>
        </div>
        <Switch
          checked={Boolean(consultant)}
          aria-label="Add consultant details"
          onCheckedChange={(checked) =>
            update({
              consultant: checked
                ? { fees: draft.fees, hoursSameAsEstablishment: true, timings: emptyTimings() }
                : null,
            })
          }
        />
      </div>

      {consultant && (
        <>
          <Field
            label={`Consultation fee (${symbol})`}
            htmlFor="consultantFees"
            required
            error={errors.consultant}
          >
            <Input
              id="consultantFees"
              type="number"
              min={0}
              value={consultant.fees}
              onChange={(event) =>
                update({ consultant: { ...consultant, fees: event.target.value } })
              }
              placeholder="1000"
              className="h-11"
            />
          </Field>

          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <p className="text-sm font-medium">Same hours as my main establishment</p>
            <Switch
              checked={consultant.hoursSameAsEstablishment}
              aria-label="Same hours as main establishment"
              onCheckedChange={(hoursSameAsEstablishment) =>
                update({ consultant: { ...consultant, hoursSameAsEstablishment } })
              }
            />
          </div>

          {!consultant.hoursSameAsEstablishment && (
            <TimingsEditor
              timings={consultant.timings}
              onChange={(timings) => update({ consultant: { ...consultant, timings } })}
            />
          )}
        </>
      )}
    </div>
  );
}
